import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { completeSetup } from '../api';
import { errorMessage } from '../lib/api';
import { useAuth } from '../auth/useAuth';
import { Spinner } from '../components/Spinner';
import { RotateCw } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

/**
 * First-run wizard. Shown only while the platform has zero accounts (gated by
 * App.tsx). Creates the first account (owner of a personal workspace) and
 * auto-logs-in. Provider/GitHub secrets are NOT collected here — they're
 * managed on the Settings page after login.
 */
export function SetupPage() {
  useDocumentTitle('Setup');
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const finish = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 10) {
      setError('Password must be at least 10 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await completeSetup({ email: email.trim(), password });
      setSession(res);
      // The app gate (App.tsx) caches ['setup-status'] with a 30s staleTime, so
      // without this it would still see { needsSetup: true } and bounce us right
      // back to /setup. Seed it synchronously so the gate re-opens with no flash.
      queryClient.setQueryData(['setup-status'], { needsSetup: false });
      navigate('/', { replace: true });
    } catch (err) {
      setError(errorMessage(err, 'Setup failed. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="mb-8 flex flex-col items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-ink text-white">
            <RotateCw className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="text-center">
            <h1 className="text-lg font-semibold tracking-tight text-ink">
              Welcome to aiai·loop
            </h1>
            <p className="mt-1 text-sm text-ink-muted">
              Create your account — this is the single owner login. Connect
              Claude and GitHub later in Settings.
            </p>
          </div>
        </div>

        <form onSubmit={finish} className="card space-y-4 p-6">
          <div>
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 10 characters"
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="confirm">
              Confirm password
            </label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              className="input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter password"
              required
            />
          </div>

          {error && (
            <div className="rounded-[5px] border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={submitting}
          >
            {submitting && <Spinner />}
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>
      </div>
    </div>
  );
}
